import httpClient from './httpClient';
import { CaseAdditionalInfo, CaseDto } from '../types/backendType';

//Get /case-id/assignment
//
// The backend defaults to active-only (?active=true). Pass
// includeArchived=true to also get archived (active=false) cases — used by
// the Manage Cases dialog. The normal header/case-switcher path uses the
// active-only default so archived cases stay hidden there.
export async function fetchCases(
  patientId: string,
  includeArchived = false,
): Promise<CaseDto[] | undefined> {
  try {
    const response = await httpClient.get<CaseDto[]>('/case-id/assignment', {
      headers: { 'x-user-id': patientId },
      params: includeArchived ? { active: false } : undefined,
    });
    const cases = response.data;
    return cases;
  } catch (e) {
    throw new Error(`Failed to fetch user cases: ${e}`);
  }
}

//Post /case-id/assignment
export async function createCase(
  patientId: string,
  caseId: string,
  additionalInfo?: CaseAdditionalInfo,
): Promise<CaseDto> {
  try {
    const response = await httpClient.post<CaseDto>(
      '/case-id/assignment',
      {
        case_id: caseId,
        // The backend stores this as Case.AdditionalInfo (jsonb). The
        // schema is whatever the frontend writes — we use it to carry
        // the user-facing case name plus, when imported from WebPT,
        // the per-case clinical fields (ICD-10, cause, therapist).
        additional_info: additionalInfo,
      },
      {
        headers: { 'x-user-id': patientId },
      },
    );
    const caseDto = response.data;
    return caseDto;
  } catch (e) {
    throw new Error(`Failed to create case: ${e}`);
  }
}

// Patch /case-id/assignment — supports updating the case's AdditionalInfo
// (where we store name + EMR-imported clinical fields). Used by the EMR
// import flow to refresh fields on a case that already exists on the
// Percix side.
export async function updateCase(
  patientId: string,
  caseId: string,
  additionalInfo: CaseAdditionalInfo,
): Promise<CaseDto> {
  try {
    const response = await httpClient.patch<CaseDto>(
      '/case-id/assignment',
      {
        case_id: caseId,
        update: { additional_info: additionalInfo },
      },
      {
        headers: { 'x-user-id': patientId },
      },
    );
    return response.data;
  } catch (e) {
    throw new Error(`Failed to update case: ${e}`);
  }
}

// Patch /case-id/assignment — archives (active=false) or reactivates
// (active=true) a case. The backend hides active=false cases from the
// default GET, so archiving removes the case from the View-CaseID switcher
// without deleting any data. NOTE: on the backend, archiving also end-dates
// any open treatment plans on the case; reactivating does not restore those
// end dates (see treatment-plan-handler UpdateCase.updateActive).
export async function setCaseActive(
  patientId: string,
  caseId: string,
  active: boolean,
): Promise<CaseDto> {
  try {
    const response = await httpClient.patch<CaseDto>(
      '/case-id/assignment',
      {
        case_id: caseId,
        update: { active },
      },
      {
        headers: { 'x-user-id': patientId },
      },
    );
    return response.data;
  } catch (e) {
    throw new Error(`Failed to ${active ? 'reactivate' : 'archive'} case: ${e}`);
  }
}

// PATCH /case-id/active — sets the supplied case as the patient's active
// plan case (and implicitly clears any previously active one in the same
// transaction). Returns the full updated case list so the caller can
// refresh local state without a separate GET. See
// docs/Architecture/active-plan-selector.md for the full rationale.
export async function setActivePlanCase(
  patientId: string,
  caseId: string,
): Promise<CaseDto[]> {
  try {
    const response = await httpClient.patch<CaseDto[]>(
      '/case-id/active',
      { case_id: caseId },
      {
        headers: { 'x-user-id': patientId },
      },
    );
    return response.data;
  } catch (e) {
    throw new Error(`Failed to set active plan case: ${e}`);
  }
}
